import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Calendar, Users, Sparkles, Check, Wand2, Send } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { NeonSelect } from '@/components/NeonSelect';

const PLATFORMS = [
  { id: 'meta', name: 'Meta', desc: 'Facebook + Instagram' },
  { id: 'google', name: 'Google Ads', desc: 'Search + Display + YouTube' },
  { id: 'tiktok', name: 'TikTok', desc: 'Short-form video' },
  { id: 'x', name: 'X · Twitter', desc: 'Conversation amplification' },
];
const OBJECTIVES = ['Awareness', 'Traffic', 'Engagement', 'Leads', 'App promotion', 'Sales'];

export default function CampaignBuilder() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [copyVariants, setCopyVariants] = useState(null);
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem('boosta_campaign_draft');
      if (saved) return JSON.parse(saved);
    } catch (e) { console.warn('Draft restore failed', e); }
    return { name: '', objective: 'Sales', platform: 'meta', budget: 500, daily_budget: 50, audience: '', notes: '' };
  });
  useEffect(() => {
    localStorage.setItem('boosta_campaign_draft', JSON.stringify(form));
  }, [form]);
  const upd = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  // Meta publish (draft) state
  const [meta, setMeta] = useState({ connected: false, ad_accounts: [], pages: [] });
  const [adAccount, setAdAccount] = useState('');
  const [pageId, setPageId] = useState('');
  const [publishMeta, setPublishMeta] = useState(true);

  useEffect(() => {
    if (form.platform !== 'meta') return;
    (async () => {
      try {
        const { data } = await api.get('/business/meta/ad-accounts');
        setMeta(data);
        if (data.ad_accounts?.length) setAdAccount(data.ad_accounts[0].id);
        if (data.pages?.length) setPageId(data.pages[0].id);
      } catch (e) { console.error('Meta targets load failed', e); }
    })();
  }, [form.platform]);

  const generateCopy = async () => {
    if (!form.name || !form.audience) {
      toast.error('Add campaign name and audience first.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/ai/copy', { product: form.name, audience: form.audience, platform: form.platform, tone: 'premium' });
      setCopyVariants(data.variants || []);
      toast.success('Copy variants generated. 5 ZAP used.');
      await refreshUser();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/campaigns', {
        ...form, budget: parseFloat(form.budget) || 0,
        daily_budget: parseFloat(form.daily_budget) || 0,
      });
      localStorage.removeItem('boosta_campaign_draft');
      const isMeta = form.platform === 'meta' && meta.connected && publishMeta && adAccount;
      if (isMeta) {
        try {
          const res = await api.post(`/campaigns/${data.campaign_id}/publish-meta`, {
            ad_account_id: adAccount,
            page_id: pageId || null,
          });
          const stepsMsg = Object.entries(res.data.steps || {}).map(([k, v]) => `${k}: ${v}`).join(' · ');
          if (res.data.warning) toast.warning(res.data.warning);
          else toast.success(`Published to Meta as PAUSED draft. ${stepsMsg}`);
        } catch (e) {
          toast.error(`Draft saved, but Meta publish failed: ${formatApiError(e.response?.data?.detail)}`);
        }
      } else {
        toast.success('Campaign saved as draft. Launch from Campaigns.');
      }
      nav('/campaigns');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-7 max-w-4xl" data-testid="builder-root">
      <button onClick={()=>nav(-1)} className="text-sm text-white/60 inline-flex items-center gap-1 hover:text-white"><ArrowLeft className="w-4 h-4"/> Back</button>
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Campaign builder</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Configure your launch</h1>
      </div>

      <div className="flex gap-2">
        {[1,2,3].map(s => (
          <div key={s} className={`flex-1 h-1 rounded-full ${s<=step?'bg-boosta-cyan':'bg-white/10'} transition`}/>
        ))}
      </div>

      {step===1 && (
        <div className="glass rounded-2xl p-7 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Campaign name</label>
            <input value={form.name} onChange={upd('name')} data-testid="builder-name" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-boosta-cyan/50 outline-none" placeholder="e.g. UK Spring Launch — Premium SKU"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Objective</label>
              <NeonSelect value={form.objective} onChange={upd('objective')} data-testid="builder-objective"
                options={OBJECTIVES.map(o=>({value:o,label:o}))}/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Platform</label>
              <NeonSelect value={form.platform} onChange={upd('platform')} data-testid="builder-platform"
                options={PLATFORMS.map(p=>({value:p.id,label:p.name}))}/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Total budget (£)</label>
              <input type="number" value={form.budget} onChange={upd('budget')} data-testid="builder-budget" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Daily budget (£)</label>
              <input type="number" value={form.daily_budget} onChange={upd('daily_budget')} data-testid="builder-daily-budget" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm"/>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Audience description</label>
            <textarea rows="3" value={form.audience} onChange={upd('audience')} data-testid="builder-audience" placeholder="UK women 28–45, urban, premium beauty buyers, interested in skincare" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm resize-none"/>
          </div>
          <div className="flex justify-end">
            <button onClick={()=>setStep(2)} className="btn-primary rounded-full px-6 py-2.5 text-sm" data-testid="builder-next-1">Continue</button>
          </div>
        </div>
      )}

      {step===2 && (
        <div className="glass rounded-2xl p-7 space-y-5">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-display font-bold text-xl">AI copy variants</div>
              <div className="text-sm text-white/55 mt-1">Generate three premium ad-copy options powered by GPT-5.2.</div>
            </div>
            <button disabled={busy} onClick={generateCopy} className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="builder-generate-copy">
              <Wand2 className="w-4 h-4"/> {busy ? 'Generating…' : 'Generate (5 ZAP)'}
            </button>
          </div>
          {copyVariants && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {copyVariants.map((v, i) => (
                <div key={`${v.headline || 'variant'}-${i}`} className="border border-white/10 rounded-xl p-4 bg-black/30">
                  <div className="text-xs text-boosta-cyan uppercase tracking-wider font-bold mb-2">Variant {i+1}</div>
                  <div className="font-display font-bold text-base mb-2">{v.headline}</div>
                  <div className="text-sm text-white/65 leading-relaxed">{v.body}</div>
                  <div className="text-xs mt-3 text-boosta-yellow uppercase tracking-wider font-bold">CTA: {v.cta}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between">
            <button onClick={()=>setStep(1)} className="btn-ghost rounded-full px-5 py-2.5 text-sm">Back</button>
            <button onClick={()=>setStep(3)} className="btn-primary rounded-full px-6 py-2.5 text-sm" data-testid="builder-next-2">Continue</button>
          </div>
        </div>
      )}

      {step===3 && (
        <div className="glass rounded-2xl p-7 space-y-5">
          <div className="font-display font-bold text-xl">Review & save</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div><span className="text-white/45">Name:</span> {form.name}</div>
            <div><span className="text-white/45">Objective:</span> {form.objective}</div>
            <div><span className="text-white/45">Platform:</span> <span className="uppercase">{form.platform}</span></div>
            <div><span className="text-white/45">Budget:</span> £{form.budget} (£{form.daily_budget}/day)</div>
            <div className="col-span-2"><span className="text-white/45">Audience:</span> {form.audience || '—'}</div>
          </div>
          <div className="rounded-xl border border-boosta-cyan/20 bg-boosta-cyan/5 p-4 text-sm text-white/75 flex items-start gap-3">
            <Check className="w-4 h-4 text-boosta-emerald mt-0.5"/>
            Campaign will be saved as a draft. Launch from the Campaigns page (costs 50 ZAP).
          </div>

          {form.platform === 'meta' && (
            <div className="rounded-xl border border-boosta-yellow/20 bg-boosta-yellow/5 p-5 space-y-4" data-testid="meta-publish-panel">
              <div className="flex items-center gap-2 font-display font-bold text-base">
                <Send className="w-4 h-4 text-boosta-yellow"/> Publish to Meta (draft)
              </div>
              {!meta.connected ? (
                <div className="text-sm text-white/60" data-testid="meta-not-connected">
                  Meta account not connected. Connect it in <span className="text-boosta-cyan">Business Hub</span> to publish a paused draft campaign directly to Ads Manager.
                </div>
              ) : meta.ad_accounts.length === 0 ? (
                <div className="text-sm text-white/60" data-testid="meta-no-accounts">
                  No ad accounts found on your connected Meta profile.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Ad account</label>
                      <NeonSelect value={adAccount} onChange={e=>setAdAccount(e.target.value)} data-testid="meta-ad-account"
                        options={meta.ad_accounts.map(a=>({value:a.id,label:`${a.name || a.id} (${a.currency || '—'})`}))}/>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Facebook page (optional)</label>
                      <NeonSelect value={pageId} onChange={e=>setPageId(e.target.value)} data-testid="meta-page"
                        options={[{value:'',label:'— No creative/ad —'}, ...meta.pages.map(p=>({value:p.id,label:p.name}))]}/>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-white/75 cursor-pointer" data-testid="meta-publish-toggle-label">
                    <input type="checkbox" checked={publishMeta} onChange={e=>setPublishMeta(e.target.checked)} data-testid="meta-publish-toggle" className="accent-boosta-yellow"/>
                    Publish this campaign to Meta as a <span className="text-boosta-yellow font-bold">PAUSED</span> draft on save
                  </label>
                </>
              )}
            </div>
          )}

          <div className="flex justify-between">
            <button onClick={()=>setStep(2)} className="btn-ghost rounded-full px-5 py-2.5 text-sm">Back</button>
            <button disabled={busy} onClick={submit} className="btn-primary rounded-full px-6 py-2.5 text-sm disabled:opacity-50" data-testid="builder-save">
              {busy ? 'Saving…' : (form.platform === 'meta' && meta.connected && publishMeta && meta.ad_accounts.length ? 'Save & publish draft' : 'Save draft')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
